import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Clock } from "lucide-react";
import { P } from "@/lib/payments";
import { getToken } from "@/lib/store";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [st, setSt] = useState({ state: "checking" });
  useEffect(() => {
    if (!sid) { setSt({ state: "error" }); return; }
    let tries = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const r = await P.status(sid);
        if (r.payment_status === "paid") { setSt({ state: "paid", ...r }); return; }
        if (["expired", "failed"].includes(r.payment_status)) { setSt({ state: "failed", ...r }); return; }
      } catch (e) { /* retry */ }
      if (++tries < 8) setTimeout(poll, 2000); else setSt({ state: "pending" });
    };
    poll();
    return () => { stop = true; };
  }, [sid]);
  const signedIn = !!getToken();
  return (
    <Shell testId="payment-success">
      {st.state === "checking" && <><Loader2 className="w-10 h-10 animate-spin text-amber-400" /><h1 className="mt-5 text-2xl font-semibold">Confirming your payment…</h1><p className="mt-2 text-sm text-zinc-400">Hang tight — we're waiting for Stripe to confirm.</p></>}
      {st.state === "paid" && <><CheckCircle2 className="w-12 h-12 text-emerald-400" /><h1 className="mt-5 text-3xl font-semibold" data-testid="payment-paid">{st.kind === "subscription" ? "Welcome to Pro" : "Payment received"}</h1>
        <p className="mt-2 text-sm text-zinc-400">{st.kind === "subscription" ? "Your In The Pocket Pro plan is active. Thanks for backing the band." : "Thank you — the invoice is now marked as paid and a receipt is on its way from Stripe."}</p>
        {st.kind === "subscription" ? <Link to="/w/billing" className={cta} data-testid="payment-go-billing">Open your plan</Link> : signedIn ? <Link to="/w/office" className={cta}>Back to The Office</Link> : st.token ? <Link to={`/pay/${st.token}`} className={cta} data-testid="payment-view-invoice">View invoice</Link> : null}</>}
      {st.state === "pending" && <><Clock className="w-12 h-12 text-amber-400" /><h1 className="mt-5 text-2xl font-semibold">Still processing</h1><p className="mt-2 text-sm text-zinc-400">Your payment is being confirmed. You'll get a receipt by email once it clears.</p></>}
      {(st.state === "failed" || st.state === "error") && <><XCircle className="w-12 h-12 text-red-400" /><h1 className="mt-5 text-2xl font-semibold">Payment not completed</h1><p className="mt-2 text-sm text-zinc-400">Nothing was charged. You can try again from the invoice link.</p></>}
    </Shell>
  );
}

export function PaymentCancel() {
  const [params] = useSearchParams();
  const token = params.get("pay");
  return (
    <Shell testId="payment-cancel">
      <XCircle className="w-12 h-12 text-zinc-500" />
      <h1 className="mt-5 text-2xl font-semibold">Checkout cancelled</h1>
      <p className="mt-2 text-sm text-zinc-400">No charge was made. You can come back any time.</p>
      {token ? <Link to={`/pay/${token}`} className={cta} data-testid="cancel-back-invoice">Back to the invoice</Link> : getToken() ? <Link to="/w/billing" className={cta} data-testid="cancel-back-billing">Back to plans</Link> : <Link to="/" className={cta}>Home</Link>}
    </Shell>
  );
}

const cta = "mt-8 inline-flex h-11 px-6 items-center rounded-xl bg-amber-400 hover:bg-amber-300 text-black text-sm font-medium";
function Shell({ children, testId }) {
  return (
    <div className="min-h-screen bg-[#0b0b0c] text-zinc-100 flex items-center justify-center p-6" data-testid={testId}>
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#111215] p-10 text-center flex flex-col items-center anim-up">{children}</div>
    </div>
  );
}
